<template>
  <b-container>
    <b-row>
      <b-col cols-sm="12" cols-md="6" class="container-row">
        <div class="authentication-card" :class="isMobile ? ' mobile-card' : ''">
          <div class="center-content">
            <h3>
              Create Account
            </h3>
          </div>
          <b-form-group
            label="First Name*"
            label-for="login-name"
            data-cy="first-name">
            <b-form-input id="login-name" v-model="signupRequest.Name" placeholder="Type your name" :state="isValidInput(signupRequest.Name)"></b-form-input>
            <b-form-invalid-feedback>Please enter your first name</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            label="Last Name*"
            label-for="login-last-name"
            data-cy="last-name">
            <b-form-input id="login-last-name" v-model="signupRequest.LastName" placeholder="Type your last name" :state="isValidInput(signupRequest.LastName)"></b-form-input>
            <b-form-invalid-feedback>Please enter your last name</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            label="Email*"
            label-for="login-email"
            data-cy="email-address">
            <b-form-input id="login-email" v-model="signupRequest.Email" placeholder="Type your email" :state="isValidEmail()"></b-form-input>
            <b-form-invalid-feedback>Please enter a valid email address</b-form-invalid-feedback>
          </b-form-group>
          <template v-if="!isAd">
            <b-form-group
              label="Password*"
              label-for="login-password"
              data-cy="password-signup"
              :state="signupRequest.Password.length == 0">
              <b-form-input type="password" id="login-password" v-model="signupRequest.Password" placeholder="Type a strong password" :state="isValidPassword()"></b-form-input>
              <b-form-invalid-feedback>Please enter a valid password</b-form-invalid-feedback>
              <b-row class="mt-2">
                <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneLowercase) }">
                  <span class="when-closed">
                    <b-icon icon="check" class="icon"></b-icon>
                  </span>
                  1 lowercase letter
                </b-col>
                <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneUppercase) }">
                  <span class="when-closed">
                    <b-icon icon="check" class="icon"></b-icon>
                  </span>
                  1 uppercase letter
                </b-col>
              </b-row>
              <b-row>
                <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneDigit) }">
                  <span class="when-closed">
                    <b-icon icon="check" class="icon"></b-icon>
                  </span>
                  1 number
                </b-col>
                <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.oneSymbol) }">
                  <span class="when-closed">
                    <b-icon icon="check" class="icon"></b-icon>
                  </span>
                  1 symbol ($ % @...)
                </b-col>
              </b-row>
              <b-row>
                <b-col class="help-text" :class="{ disabled: !validatePasswordRegex(validators.minEightChars) }">
                  <span class="when-closed">
                    <b-icon icon="check" class="icon"></b-icon>
                  </span>
                  At least 8 characters long
                </b-col>
              </b-row>
            </b-form-group>
            <b-form-group
              label="Confirm password*"
              label-for="login-confirm-password"
              data-cy="password-confirm">
              <b-form-input type="password" id="login-confirm-password" v-model="passwordConfirmation" placeholder="Confirm your password" :state="isValidConfirmPassword()"></b-form-input>
              <b-form-invalid-feedback>The passwords are different</b-form-invalid-feedback>
            </b-form-group>
          </template>
          <template v-if="!shouldUseInvitationCode && _isSalesRepSite(site.Name)">
            <div>
              <label>Sales representative id(s)</label>
            </div>
            <b-row
              class="mt-1 ml-0 mr-0 mb-1"
              v-for="(salesRepId, index) in signupRequest.SalesRepIds"
              v-bind:key="'sales_rep_id' + index">
              <b-col sm="11" class="pl-0">
                <b-form-input id="login-sales-representative-id" v-model="signupRequest.SalesRepIds[index]" placeholder="Type your sales representative id" :state="isValidInput(salesRepId)"></b-form-input>
                <b-form-invalid-feedback>Please enter a sales representative id</b-form-invalid-feedback>
              </b-col>
              <b-col sm="1" class="text-center">
                <b-button @click="removeSalesRep(index)"><b-icon-trash></b-icon-trash></b-button>
              </b-col>
            </b-row>
            <div class="mt-2 mb-2 text-right">
              <a @click="addSalesRepresentative()" role="button" data-cy="add-customer-id" class="add-section-table-action">Add another sales representative id <b-icon icon="plus-square" variant="info" aria-hidden="true"></b-icon></a>
            </div>
          </template>
          <template v-if="!shouldUseInvitationCode && _isCustomerSite(site.Name)">
            <div>
              <label>Customer id(s)</label>
            </div>
            <b-row
              class="mt-1 ml-0 mr-0 mb-1"
              v-for="(customerId, index) in signupRequest.CustomerNumbers"
              v-bind:key="'sales_rep_id' + index">
              <b-col sm="11" class="pl-0">
                <b-form-input id="login-sales-customer-id" data-cy="sign-up-sales-customer-id" v-model="signupRequest.CustomerNumbers[index]" placeholder="Type your customer id" :state="isValidInput(customerId)"></b-form-input>
                <b-form-invalid-feedback>Please enter a customer id</b-form-invalid-feedback>
              </b-col>
              <b-col sm="1" class="text-center">
                <b-button @click="removeCustomer(index)"><b-icon-trash></b-icon-trash></b-button>
              </b-col>
            </b-row>
            <div class="mt-2 mb-2 text-right">
              <a @click="addCustomer()" role="button" class="add-section-table-action">Add another customer id <b-icon icon="plus-square" variant="info" aria-hidden="true"></b-icon></a>
            </div>
          </template>
          <b-form-checkbox v-if="false" v-model="shouldUseInvitationCode">I have an invitation code</b-form-checkbox>
          <b-form-group v-if="shouldUseInvitationCode" label="Invitation code" label-for="login-invitation-code">
            <b-form-input id="login-invitation-code" v-model="signupRequest.InvitationCode" placeholder="Type your invitation code" :state="isValidInput(signupRequest.InvitationCode)"></b-form-input>
            <b-form-invalid-feedback>Please enter an invitation code</b-form-invalid-feedback>
          </b-form-group>
          <div>
            <b-button @click="onSignup()" variant="primary" class="full-width" :disabled="isLoading">
              <template v-if="isLoading">
                <loading loadingText="Submitting"></loading>
              </template>
              <template v-else>
                Create account
              </template>
            </b-button>
          </div>
          <div class="p-3"></div>
          <div class="p-3"></div>
          <div>
            <span>Do you have an account? <a href="/authentication/login">Log in</a></span>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { ISignupRequest } from '@/store/security/types'
import { TPIException } from '@/store/editor/types'
import Loading from '@/components/utility/loading.vue'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'
import MxSite from '@/mixins/site'

// const errors = {
//   EmptyEmail: 'Please enter an email',
//   EmptyPassword: 'Please enter a password',
//   InvalidPassword: 'Please enter a valid password',
//   EmptyInvitationCode: 'Please enter an invitation code',
//   EmptySalesRepId: 'Please enter your sales representative id',
//   EmptyName: 'Please enter your first name',
//   EmptyLastName: 'Please enter your last name',
// }

const passwordValidators = {
  minEightChars: /^.{8,}$/,
  oneLowercase: /^(?=.*?[a-z])/,
  oneUppercase: /^(?=.*?[A-Z])/,
  oneDigit: /^(?=.*?[0-9])/,
  oneSymbol: /^(?=.*?[" !"#$%&'()+,-.*/:;<=>?@[\]^_`{|}~"])/,
}

const emailRegex = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/

@Component({
  components: {
    Loading,
  },
})

export default class extends Mixins(MxSite) {
  public isMobile = isMobile()
  public validators = passwordValidators
  public passwordConfirmation = ''
  public isLoading = false

  public didSubmit = false

  public shouldUseInvitationCode = false

  public signupRequest: ISignupRequest = {
    Email: '',
    Password: '',
    Challenge: '',
    InvitationCode: '',
    LastName: '',
    Name: '',
    SalesRepIds: [
      '',
    ],
    CustomerNumbers: [
      '',
    ],
  }

  public created() {
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
  }

  public async onSignup() {
    const { signupRequest } = this
    this.didSubmit = true
    this.isLoading = true

    if (this.shouldUseInvitationCode) {
      signupRequest.SalesRepIds = ['']
      signupRequest.CustomerNumbers = ['']
    } else {
      signupRequest.InvitationCode = ''
    }

    if (!this.isFormValid) {
      this.isLoading = false
      return
    }

    const { $router } = this

    await this.$recaptchaLoaded()

    signupRequest.Challenge = await this.$recaptcha('signup')

    this.signup({ signupRequest }).then(() => {
      // TODO JM: redirect user to previous page
      this.toastSuccess('User created successfully')
      if (this.shouldUseInvitationCode) {
        $router.push('/')
      } else {
        this.logout().then(() => {
          $router.push('/authentication/pending-approval')
        })
      }
    }).catch(({ Message }: TPIException) => {
      this.toastError(Message)
    }).finally(() => {
      this.isLoading = false
    })
  }

  public addSalesRepresentative() {
    const { signupRequest } = this
    signupRequest.SalesRepIds.push('')
  }

  public addCustomer() {
    const { signupRequest } = this
    signupRequest.CustomerNumbers.push('')
  }

  public removeSalesRep(index: number) {
    const { signupRequest } = this

    if (signupRequest.SalesRepIds.length === 1) {
      signupRequest.SalesRepIds = ['']
    } else if (signupRequest.SalesRepIds.length > index) {
      signupRequest.SalesRepIds.splice(index, 1)
    }
  }

  public removeCustomer(index: number) {
    const { signupRequest } = this

    if (signupRequest.CustomerNumbers.length === 1) {
      signupRequest.CustomerNumbers = ['']
    } else if (signupRequest.CustomerNumbers.length > index) {
      signupRequest.CustomerNumbers.splice(index, 1)
    }
  }

  public isValidEmail() {
    return !this.didSubmit
      ? null
      : emailRegex.test(this.signupRequest.Email ?? '')
  }

  public isValidPassword() {
    if (!this.didSubmit) {
      return null
    }

    return this.validatePasswordRegex(this.validators.minEightChars) &&
      this.validatePasswordRegex(this.validators.oneDigit) &&
      this.validatePasswordRegex(this.validators.oneLowercase) &&
      this.validatePasswordRegex(this.validators.oneUppercase) &&
      this.validatePasswordRegex(this.validators.oneSymbol)
  }

  public validatePasswordRegex(regex: any): boolean {
    return regex.test(this.signupRequest.Password)
  }

  public isValidConfirmPassword() {
    return !this.didSubmit || this.signupRequest.Password === ''
      ? null
      : this.passwordConfirmation === this.signupRequest.Password
  }

  public isValidInput(value: string): boolean | null {
    return !this.didSubmit
      ? null
      : (value ?? '').length > 0
  }

  private get isAd() {
    return this.currentUser.AuthProviderType === 'AD'
  }

  private get isFormValid(): boolean {
    return (this.isValidInput(this.signupRequest.Name) ?? false) &&
      (this.isValidInput(this.signupRequest.LastName) ?? false) &&
      ((!this.shouldUseInvitationCode || this.isValidInput(this.signupRequest.InvitationCode)) ?? false) &&
      ((this.shouldUseInvitationCode || this.areSalesRepIdsValid || this.CustomerNumbersValid) ?? false) &&
      (this.isAd || (this.isValidPassword() ?? false)) &&
      (this.isAd || (this.isValidConfirmPassword() ?? false))
  }

  private get areSalesRepIdsValid() {
    for (let index = 0; index < this.signupRequest.SalesRepIds.length; index++) {
      if (this.signupRequest.SalesRepIds[index] === '') return false
    }

    return true && this._isSalesRepSite(this.site.Name) && this.signupRequest.SalesRepIds.length > 0
  }

  private get CustomerNumbersValid() {
    for (let index = 0; index < this.signupRequest.CustomerNumbers.length; index++) {
      if (this.signupRequest.CustomerNumbers[index] === '') return false
    }

    return true && this._isCustomerSite(this.site.Name) && this.signupRequest.CustomerNumbers.length > 0
  }
}
</script>
<style>
@import '../../styles/authentication.css'
</style>
